import React, { useReducer, useContext, useCallback, useState, useEffect } from 'react';
import axios from 'axios';

import { formatDOB } from '../helpers.js';
import logger from '../utils/logger';
import logError from '../utils/logError';

import reducer from './reducer.js';
import { useAppConfig } from './AppConfigContext.jsx';
import {
  ACQUIRE_DATA_BEGIN,
  ACQUIRE_DATA_SUCCESS,
  SETUP_USER_BEGIN,
  SETUP_USER_SUCCESS,
  SETUP_USER_ERROR,
  CLEAR_ALERT,
  CLEAR_VALUES,
  DISPLAY_ALERT,
  GET_TESTS_BEGIN,
  GET_TESTS_SUCCESS,
  GET_TEST_TYPES_BEGIN,
  GET_TEST_TYPES_SUCCESS,
  SAVE_TEST_BEGIN,
  SAVE_TEST_SUCCESS,
  SAVE_TEST_ERROR,
  SHOW_REPORT_BEGIN,
  HANDLE_CHANGE,
  HANDLE_TOGGLE_COMPATIBILITY,
  SET_ACQUIRE_RUNNING,
  SET_MOCKED_TEST,
  SET_BYPASS_PHONE,
} from './actions.js';

const initialState = {
  userLoading: true,
  isLoading: false,
  showAlert: false,
  alertText: '',
  alertType: '',
  tests: [],
  testTypes: [],
  defaultTestId: null,
  numOfPages: 1,
  testsTotal: 0,
  page: 0,
  testId: '',
  firstName: '',
  lastName: '',
  birthDate: '',
  gender: '',
  testType: '',
  eye: '',
  createdBy: '',
  data: [],
  reportData: '',
  riskIndication: '',
  status: '',
  searchFirstName: '',
  searchLastName: '',
  searchDateOfBirth: '',
  bypassCompatibility: false,
  isLocalHost: false,
  isAcquireRunning: false,
  isTestMocked: localStorage.getItem('isTestMocked') === 'true',
  isPhoneBypassed: localStorage.getItem('isPhoneBypassed') === 'true',
};
const AppContext = React.createContext();

const AppProvider = ({ children }) => {
  const { api, features } = useAppConfig();
  const [state, dispatch] = useReducer(reducer, initialState, (state) => ({
    ...state,
    bypassCompatibility: features.bypassCompatibilityDefault,
    isLocalHost: features.isLocalHost,
  }));
  const [globalHealthStatus, setGlobalHealthStatus] = useState('checking');
  const testBaseUrl = api.test.baseUrl ? `${api.test.baseUrl}/` : '';
  const reportBaseUrl = api.report.baseUrl ? `${api.report.baseUrl}/` : '';
  const acquireBaseUrl = api.acquire.baseUrl ? `${api.acquire.baseUrl}/` : '';
  // Test
  const testFetch = axios.create({
    baseURL: testBaseUrl,
    withCredentials: true,
  });
  // to ensure localStorage values are updated when state changes
  useEffect(() => {
    localStorage.setItem('isTestMocked', state.isTestMocked);
    localStorage.setItem('isPhoneBypassed', state.isPhoneBypassed);
  }, [state.isTestMocked, state.isPhoneBypassed]);

  testFetch.interceptors.request.use(
    (req) => {
      req.headers['Content-Type'] = 'application/json; charset=utf-8';
      // Include the CSRF token in the request headers
      const csrfToken = localStorage.getItem('csrfToken');

      if (csrfToken) {
        req.headers['X-CSRF-Token'] = csrfToken;
      }
      return req;
    },
    (error) => {
      return Promise.reject(error);
    },
  );

  testFetch.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      return Promise.reject(error);
    },
  );

  // Report
  const reportFetch = axios.create({
    baseURL: reportBaseUrl,
    responseType: 'blob',
    withCredentials: true,
  });

  reportFetch.interceptors.request.use(
    (req) => {
      req.headers['Content-Type'] = 'application/json; charset=utf-8';
      req.headers['Accept'] = '*/*';
      return req;
    },
    (error) => {
      return Promise.reject(error);
    },
  );

  reportFetch.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      logger.error(logError(error, { action: 'reportFetchResponseError' }));
      return Promise.reject(error);
    },
  );

  // Data Acquire
  const dataAcquire = axios.create({
    baseURL: acquireBaseUrl,
    withCredentials: true,
  });

  dataAcquire.interceptors.request.use(
    (req) => {
      req.headers['Content-Type'] = 'application/json; charset=utf-8';
      req.headers['Accept'] = '*/*';
      return req;
    },
    (error) => {
      return Promise.reject(error);
    },
  );

  dataAcquire.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      logger.error(logError(error, { action: 'dataAcquireResponseError' }));
      return Promise.reject(error);
    },
  );

  const displayAlert = () => {
    dispatch({ type: DISPLAY_ALERT });
    clearAlert();
  };

  const clearAlert = () => {
    setTimeout(() => {
      dispatch({ type: CLEAR_ALERT });
    }, 3000);
  };

  const handleChange = ({ name, value }) => {
    dispatch({ type: HANDLE_CHANGE, payload: { name, value } });
  };

  const clearValues = () => {
    dispatch({ type: CLEAR_VALUES });
  };

  const acquireData = async (testId, trajectoryName) => {
    dispatch({ type: ACQUIRE_DATA_BEGIN });
    try {
      let url = `/data/acquire/${testId}/${trajectoryName}`;
      const { data } = await dataAcquire.get(url);
      const { recordingId } = data;

      dispatch({
        type: ACQUIRE_DATA_SUCCESS,
        payload: { data },
      });
    } catch (error) {
      logger.error(logError(error, { action: 'acquireData' }));
    }
  };

  const saveTest = async (testData) => {
    dispatch({ type: SAVE_TEST_BEGIN });
    try {
      const {
        testId,
        firstName,
        lastName,
        dateOfBirth,
        gender,
        familyHistory,
        eye,
        notes,
        odSphere,
        odCylinder,
        odAddition,
        odAcuity,
        osSphere,
        osCylinder,
        osAddition,
        osAcuity,
        testType,
        createdBy,
        // clearValues,
      } = testData;

      let dob = dateOfBirth;

      // Convert to Date object if necessary
      if (!(dob instanceof Date)) {
        dob = new Date(dob);
      }

      const data = {
        testId: testId,
        firstName: firstName,
        lastName: lastName,
        dateOfBirth: formatDOB(dob),
        gender: gender,
        familyHistory: familyHistory,
        eye: eye,
        // language is intentionally omitted here until the backend /tests endpoint supports it.
        // It is already sent to /data/acquire where it is consumed for stimulus selection.
        notes: notes,
        correction: {
          odSphere: odSphere,
          odCylinder: odCylinder,
          odAddition: odAddition,
          odAcuity: odAcuity,
          osSphere: osSphere,
          osCylinder: osCylinder,
          osAddition: osAddition,
          osAcuity: osAcuity,
        },
        testType: {
          value: testType.value,
          label: testType.label,
          disabled: testType.disabled,
          trajectories: testType.trajectories,
          ...(testType.testId != null && { testId: testType.testId }),
          ...(testType.earlyStop != null && { earlyStop: testType.earlyStop }),
          ...(testType.reportVariant != null && { reportVariant: testType.reportVariant }),
          // Persist behavior with the test-definition snapshot.
          ...(testType.eyeMode != null && { eyeMode: testType.eyeMode }),
        },
        createdBy: createdBy,
        hasPerSessionProcessing: testData.hasPerSessionProcessing ?? false,
      };
      await testFetch.post('/tests', data);
      dispatch({ type: SAVE_TEST_SUCCESS });
      dispatch({ type: CLEAR_VALUES });
    } catch (error) {
      if (error.response?.status !== 401) {
        dispatch({
          type: SAVE_TEST_ERROR,
          payload: {
            msg:
              error.response?.data?.msg ||
              error.response?.data?.message ||
              error.message ||
              'Failed to save test',
          },
        });
      }
      throw error;
    } finally {
      clearAlert();
    }
  };

  //to check if there can be more than 100 tests:

  // const duplicateArray = (arr, n) => {
  // 	return Array.from({ length: n }, () => [...arr]).flat();
  //   };

  const getTests = useCallback(
    async (
      page = 0,
      testsPerPage = 10,
      sortBy = 'testDate',
      sortOrder = 'desc',
      searchParams = {},
    ) => {
      const { dateOfBirth, firstName, lastName } = searchParams;
      let url = `tests?page=${page}&testsPerPage=${testsPerPage}&sortBy=${sortBy}&sortOrder=${sortOrder}`;

      if (firstName) url += `&firstName=${firstName}`;
      if (lastName) url += `&lastName=${lastName}`;
      if (dateOfBirth) url += `&dateOfBirth=${dateOfBirth}`;

      dispatch({ type: GET_TESTS_BEGIN });
      try {
        const { data } = await testFetch.get(url);
        const { tests, testsTotal } = data;
        // place duplicatedTests as payload for tests (tests: duplicated tests) and multiply testTotal by 10

        // const duplicatedTests = duplicateArray(tests, 10);

        dispatch({
          type: GET_TESTS_SUCCESS,
          payload: {
            tests,
            testsTotal: testsTotal,
          },
        });
      } catch (error) {
        logger.error(logError(error, { action: 'getTests' }));
      }
    },
    [dispatch],
  ); // assuming testFetch.get is stable and doesn't change

  const getNewTestId = async () => {
    const { data } = await testFetch.get('test-id');

    if (typeof data?.testId !== 'string' || !data.testId) {
      throw new Error('The test service returned an invalid test ID');
    }

    return data.testId;
  };

  const getTestTypes = async () => {
    let url = `test-types`;
    dispatch({ type: GET_TEST_TYPES_BEGIN });
    try {
      const { data } = await testFetch.get(url);
      const { testTypes, defaultTestId } = data;

      dispatch({
        type: GET_TEST_TYPES_SUCCESS,
        payload: {
          testTypes,
          defaultTestId,
        },
      });
    } catch (error) {
      logger.error(logError(error, { action: 'getTestTypes' }));
    }
  };
  const deleteTestStep = async (testId, stepId) => {
    const csrfToken = localStorage.getItem('csrfToken');

    const headers = {
      'Content-Type': 'application/json; charset=utf-8',
      'X-CSRF-Token': csrfToken, // Add the CSRF token to the request headers
    };

    try {
      if (!api.report.baseUrl) {
        throw new Error('Missing report base URL');
      }
      const responseData = await axios.delete(
        `${api.report.baseUrl}/data/sessions/${testId}/${stepId}`,
        {
          responseType: 'json',
          withCredentials: true,
          headers: headers,
        },
      );
    } catch (error) {
      logger.error(
        logError(error, {
          action: 'deleteTestStep',
          status: error?.response?.status,
          statusText: error?.response?.statusText,
        }),
      );
    }
  };

  const setupUser = async ({ currentUser, endPoint, alertText }) => {
    dispatch({ type: SETUP_USER_BEGIN });
    try {
      const { data } = await axios.post(`/api/v1/auth/${endPoint}`, currentUser);

      const { user } = data;
      dispatch({
        type: SETUP_USER_SUCCESS,
        payload: { user, alertText },
      });
    } catch (error) {
      dispatch({
        type: SETUP_USER_ERROR,
        payload: { msg: error.response.data.msg },
      });
    }
    clearAlert();
  };

  const showReport = async (testId) => {
    dispatch({ type: SHOW_REPORT_BEGIN });

    let url = `reports/${testId}`;
    let reportURL;

    try {
      const data = await reportFetch.get(url);
      const file = new Blob([data], { type: 'application/pdf' });
      reportURL = URL.createObjectURL(file);
    } catch (error) {
      logger.error(logError(error, { action: 'showReport' }));
      // Here handle the error, for instance by updating some state
      this.setState({ error: error.message });
    }

    return reportURL;
  };

  const handleSelect = async (event) => {
    dispatch({
      type: HANDLE_CHANGE,
      payload: event.target.value,
    });
  };
  const toggleCompatibility = async (event) => {
    dispatch({
      type: HANDLE_TOGGLE_COMPATIBILITY,
      payload: event,
    });
    localStorage.setItem('compatibility', event);
  };

  const setAcquireRunning = (isRunning) => {
    dispatch({ type: SET_ACQUIRE_RUNNING, payload: isRunning });
  };
  const toggleMockTest = (isTestMocked) => {
    dispatch({ type: SET_MOCKED_TEST, payload: isTestMocked });
    localStorage.setItem('isTestMocked', isTestMocked);
  };
  const togglePhoneBypass = (isPhoneBypassed) => {
    dispatch({ type: SET_BYPASS_PHONE, payload: isPhoneBypassed });
    localStorage.setItem('isPhoneBypassed', isPhoneBypassed);
  };

  return (
    <AppContext.Provider
      value={{
        ...state,
        acquireData,
        getTests,
        getNewTestId,
        getTestTypes,
        saveTest,
        clearValues,
        showReport,
        setupUser,
        handleChange,
        handleSelect,
        toggleCompatibility,
        setAcquireRunning,
        setGlobalHealthStatus,
        globalHealthStatus,
        toggleMockTest,
        togglePhoneBypass,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

const useAppContext = () => {
  return useContext(AppContext);
};

export { AppProvider, initialState, useAppContext };
